/** @jsx jsx */
import { jsx } from "@emotion/core";
import * as React from "react";
import { dashboardStyles, partnerSpeaksStyles } from "./styles";
import {
  OnboardingScreens,
  StepsStatus,
  StepsKey,
  StepsInfo,
} from "./interface";
import { useIsMobile, useIsTab } from "../../hooks/useIsMobile";
import { Steps } from "antd";
import { DisplayInfo } from "../../atoms/DisplayInfo";
import {
  DisplayInfoProps,
  WidgetType,
} from "../../atoms/DisplayInfo/interface";
import { useSTR } from "react-str";
import { URL } from "../../API/urlEndpoints";
import { getData, postData } from "../../utils/fetchers";
import { CustomButton } from "../../atoms/CustomButton";
import {
  CustomButtonColor,
  CustomButtonTypes,
} from "../../atoms/CustomButton/interface";
import { singleSectionStyles } from "../../commons/styles";
import {
  portalWorkingWaysHeading,
  portalWorkingWays,
  PropValuesEnum,
  color,
  routeURL,
} from "../../constants/commons";
import {
  ResponsiveWidget,
  ResponsiveWidgetItem,
} from "../../components/ResponsiveWidget";
import { PartnersSpeak } from "../../components/PartnersSpeak";
import { useHistory } from "react-router-dom";
import { WelcomeAboardPage } from "../WelcomeAboardPage";
import { useUserContext } from "../../hooks/cookieHooks";
import { useCookies } from "react-cookie";
import { ErrorComponent } from "../../molecules/ErrorComponent";
import { showDisplayHeadingAndContentFromLoadingStatus } from "../../utils/helpers";
import { ShowMessage } from "../../components/ShowMessage";
import { LeadState, Profession } from "../../commons/interfaces";
import _ from "lodash";

const { Step } = Steps;

export const Dashboard: React.FunctionComponent = () => {
  const isMobile = useIsMobile();
  const isTablet = useIsTab();
  //Initialising the Cookei
  const [, setCookie] = useCookies(["useMiscellaneousHook"]);
  const user = useUserContext();
  const [dashboardData, setDashboardData] = React.useState<StepsInfo>({});
  const [leadState, setLeadState] = React.useState<LeadState>();
  const [professionIsEnabled, setProfessionIsEnabled] = React.useState<boolean>(
    true
  );
  const stepsKeyArr = [StepsKey.STAGE_1, StepsKey.STAGE_2, StepsKey.STAGE_3];
  const routeHistory = useHistory();

  const [
    ,
    {
      result: getDashboardDataResult,
      revalidator: refetchDashboardData,
      loading: dashboardDataisLoading,
      error: getDashboardDataError,
    },
  ] = useSTR(getData);

  // Hook to show the WelcomeAboard component or not
  const showFinalCongratulatoryComponent = React.useMemo(() => {
    if (
      dashboardData &&
      _.keys(dashboardData).length > 0 &&
      dashboardData[StepsKey.STAGE_3].status === StepsStatus.finish &&
      leadState !== LeadState.LEAD_DROPPED
    ) {
      routeHistory.push(routeURL.welcomeAboardScreen);
      return true;
    }
    return false;
  }, [dashboardData, routeHistory, leadState]);

  // Hook to write the message data onto the cookie
  React.useEffect(() => {
    dashboardData &&
      stepsKeyArr.forEach((step: any) => {
        if (
          dashboardData[step] &&
          dashboardData[step].status === StepsStatus.process
        ) {
          let currentStepMessage = {
            message: dashboardData[step].header,
          };
          setCookie("useMiscellaneousHook", currentStepMessage, { path: "/" });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardData]);

  React.useEffect(() => {
    refetchDashboardData(`${URL.getDashboardDetails(user.empId)}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!dashboardDataisLoading && getDashboardDataResult) {
      setDashboardData(getDashboardDataResult.stages);
      setLeadState(getDashboardDataResult.leadState);
      setProfessionIsEnabled(getDashboardDataResult.isProfessionEnabled);
    }
  }, [dashboardDataisLoading, getDashboardDataResult]);

  const [, { revalidator: reInitOnboarding }] = useSTR(postData);

  const handleButtonClick = React.useCallback(
    async (wdiget: DisplayInfoProps) => {
      if (
        dashboardData[stepsKeyArr[0]] &&
        dashboardData[stepsKeyArr[0]].status === StepsStatus.wait
      ) {
        await reInitOnboarding(`${URL.initiateOnboarding(user.empId)}`, {});
      }
      if (wdiget && wdiget.actionLink) {
        if (wdiget.actionLink.type === "screen") {
          switch (wdiget.actionLink.action) {
            case OnboardingScreens.REGISTRATION:
              routeHistory.push(routeURL.registrationPage);
              break;
            case OnboardingScreens.CONTRACT:
              routeHistory.push(routeURL.finalCongratulationsPage);
              break;
            case OnboardingScreens.TDS:
              routeHistory.push(routeURL.finalAgreementPage);
              break;
            case OnboardingScreens.LIVE:
              routeHistory.push(routeURL.welcomeAboardScreen);
              break;
            default:
              routeHistory.push(routeURL.registrationPage);
              break;
          }
        } else if (wdiget.actionLink.type === "url") {
          window.open(wdiget.actionLink.action, "_blank");
        }
      } else {
        routeHistory.push(routeURL.registrationPage);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dashboardData, routeHistory, stepsKeyArr, user.empId]
  );

  const renderWidgets = React.useCallback(
    (stepKeyName?: Array<string>) => {
      const keys = stepKeyName?.length ? stepKeyName : stepsKeyArr;
      return keys.map((item) => {
        return (
          !dashboardData[item].isDisabled &&
          dashboardData[item].widgets?.map((widget, key) => {
            switch (widget.widgetType) {
              case WidgetType.INFO:
                return <DisplayInfo {...widget} key={key} />;
              case WidgetType.BUTTON:
                return (
                  <CustomButton
                    color={CustomButtonColor.PRIMARY}
                    type={CustomButtonTypes.BUTTON}
                    key={key}
                    onClick={() => {
                      handleButtonClick(widget);
                    }}
                  >
                    {widget.title[0]}
                  </CustomButton>
                );
              default:
                return;
            }
          })
        );
      });
    },
    [dashboardData, handleButtonClick, stepsKeyArr]
  );

  const getDescription = React.useCallback(
    (key: string) => {
      return (
        <React.Fragment>
          {dashboardData[key].status === StepsStatus.wait && (
            <div>{dashboardData[key].title}</div>
          )}
          {renderWidgets([key])}
        </React.Fragment>
      );
    },
    [dashboardData, renderWidgets]
  );

  const renderDashboardData = React.useCallback(() => {
    return stepsKeyArr.map((item, key) => {
      const description =
        isMobile || isTablet
          ? getDescription(item)
          : dashboardData[item].status === StepsStatus.wait
          ? dashboardData[item].title
          : "";
      return (
        !dashboardData[item].isDisabled && (
          <Step
            title={dashboardData[item].header}
            description={description}
            status={dashboardData[item].status}
            key={key}
          />
        )
      );
    });
  }, [dashboardData, isMobile, isTablet, stepsKeyArr, getDescription]);

  const getPortalWorkingWays = () => {
    return portalWorkingWays.map((item, key) => {
      return (
        <ResponsiveWidgetItem key={key}>
          <img src={require(`../../assets/${item.icon}`)} alt="" />
          <div>{item.description}</div>
        </ResponsiveWidgetItem>
      );
    });
  };

  const responsiveVideoCarousel = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 1450 },
      items: 1,
      partialVisibilityGutter: 0,
    },
    desktop: {
      breakpoint: { max: 1450, min: 1170 },
      items: 1,
      partialVisibilityGutter: 0,
    },
    smallDesktop: {
      breakpoint: { max: 1170, min: 885 },
      items: 1,
      partialVisibilityGutter: 0,
    },
  };

  return (
    <div
      css={
        isMobile || isTablet
          ? dashboardStyles(
              true,
              leadState === LeadState.LEAD_LIVE ? color.whitishGray : ""
            )
          : dashboardStyles(
              false,
              leadState === LeadState.LEAD_LIVE ? color.whitishGray : ""
            )
      }
    >
      <ShowMessage
        visible={dashboardDataisLoading}
        loading={dashboardDataisLoading}
        heading={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: dashboardDataisLoading,
          })?.loadingHeading
        }
        description={
          showDisplayHeadingAndContentFromLoadingStatus({
            preLoginScreen: dashboardDataisLoading,
          })?.loadingDescription
        }
      />
      {showFinalCongratulatoryComponent ? (
        <WelcomeAboardPage />
      ) : (
        <React.Fragment>
          {!dashboardDataisLoading &&
            !getDashboardDataError &&
            !professionIsEnabled && (
              <div className="extra-info">
                <img
                  src={require(`../../assets/warning-orange-icon.svg`)}
                  alt=""
                />
                <div className="message-info">
                  We are currently not looking for onboarding{" "}
                  {user.memberships[0].context as Profession}. However you can
                  submit your details and we will get back to you when we
                  re-start the registration.
                </div>
              </div>
            )}
          <div className="dashboard-content">
            {dashboardData && dashboardData[stepsKeyArr[0]] && (
              <React.Fragment>
                {dashboardData[stepsKeyArr[0]].status === StepsStatus.wait && (
                  <React.Fragment>
                    <h2>Welcome {user.name} !</h2>
                    <span className="dashboard-message">
                      Complete the onboarding process and go Live
                    </span>
                  </React.Fragment>
                )}
                {dashboardData[stepsKeyArr[0]].status !== StepsStatus.wait &&
                  dashboardData[stepsKeyArr[2]].status === StepsStatus.wait && (
                    <h2>Complete the onboarding process</h2>
                  )}
                {dashboardData[stepsKeyArr[2]].status ===
                  StepsStatus.process && <h2>Wohoo! Only 1 step remaining</h2>}
                {dashboardData[stepsKeyArr[2]].status === StepsStatus.finish &&
                  leadState === LeadState.LEAD_DROPPED && (
                    <h2>Thank you for your interest in registering with us</h2>
                  )}
                <Steps
                  labelPlacement={"vertical"}
                  className="dashboard-steps"
                  direction={isMobile || isTablet ? "vertical" : "horizontal"}
                >
                  {renderDashboardData()}
                </Steps>
                {!(isMobile || isTablet) && renderWidgets()}
              </React.Fragment>
            )}
            {getDashboardDataError && (
              <ErrorComponent errorObj={getDashboardDataError} />
            )}
          </div>
        </React.Fragment>
      )}
      {dashboardData &&
        dashboardData[stepsKeyArr[0]] &&
        dashboardData[stepsKeyArr[0]].status !== StepsStatus.wait && (
          <div className="dashboard-more-content">
            <div
              className={`single-section mt-0 working-easy ${
                isMobile ? "mt-3" : ""
              }`}
              css={singleSectionStyles(color.white)}
            >
              <div className="header-container mb-4">
                <h5>{portalWorkingWaysHeading}</h5>
              </div>
              <ResponsiveWidget
                align={
                  isMobile ? PropValuesEnum.VERTICAL : PropValuesEnum.HORIZONTAL
                }
              >
                {getPortalWorkingWays()}
              </ResponsiveWidget>
            </div>
            <div
              className={`single-section ${isMobile || isTablet ? "" : "px-0"}`}
              css={[
                singleSectionStyles(),
                partnerSpeaksStyles(isMobile || isTablet ? true : false),
              ]}
            >
              <PartnersSpeak
                carouselViewBreakpoints={responsiveVideoCarousel}
                profession={(user.memberships[0].context as Profession) || ""}
              />
            </div>
            <div
              className="single-section"
              css={singleSectionStyles(color.white)}
            >
              {/* <CfBlogList /> */}
            </div>
          </div>
        )}
    </div>
  );
};
